import React, { useState, useEffect, useCallback } from 'react';
import { STORE_ID, sourceLabel, withCurrent } from '../../utils/monitoringSources';
import useModalWindow from '../common/useModalWindow';
import { useTranslation } from 'react-i18next';
import { FiX, FiPlus, FiTrash2, FiRotateCcw, FiStar, FiSettings, FiCode, FiShuffle, FiClock, FiActivity, FiPlay, FiSquare, FiEdit2, FiCheck, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import CollectionStatusPanel from './CollectionStatusPanel';
import { useConfirm } from '../ConfirmDialog';
import './MonitoringCriteriaModal.css';
import memoWindow from '../common/memoWindow';

const NEW_CUSTOM_KEY = '__new_custom__';

const MonitoringCriteriaModal = ({ isOpen, onClose, seed }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  // queries(기준 정의) | mappings | schedule(연결별 수집 설정) | status(현황) — 2026-10-28 사용자 요청 순서
  const [tab, setTab] = useState('queries');
  const [instances, setInstances] = useState([]);
  const [datasources, setDatasources] = useState([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState(null); // for Type A overrides (DS → 해석된 값)
  const [builtinQueries, setBuiltinQueries] = useState([]);
  const [customQueries, setCustomQueries] = useState([]);
  const [selectedQueryId, setSelectedQueryId] = useState(null); // builtin id or custom id or NEW_CUSTOM_KEY
  const [queryOverrides, setQueryOverrides] = useState([]);
  const [editDraft, setEditDraft] = useState(null); // unsaved edits
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // 매핑 탭 (builtin 전용)
  // (구조 개편으로 제거됨: selectedGroupId — 매핑 탭이 이제 좌측 selectedQueryId 를 재사용)
  const [mappings, setMappings] = useState([]);
  const [newMapping, setNewMapping] = useState({ columnAlias: '', fieldName: '', dataType: 'real' });

  // 스케줄 탭 (기존 MonitoringSettingsModal 통합)
  const [groupConfigs, setGroupConfigs] = useState([]);
  const [editingGroupId, setEditingGroupId] = useState(null);
  // 그룹별 최근 수집 이력 (summary + items) — groupId → { summary, items, columns, kind, loaded, loading }
  const [historyMap, setHistoryMap] = useState({});
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');

  const groupLabels = {
    sessions: t('monitoring:builtin.sessions'),
    sysstat: t('monitoring:builtin.sysstat'),
    wait: t('monitoring:builtin.wait'),
    memory: t('monitoring:builtin.memory')
  };

  const labelFor = (groupId) => {
    if (groupLabels[groupId]) return groupLabels[groupId];
    const c = customQueries.find(q => q.query_id === groupId);
    // 다른 DBMS 의 내장 묶음(dbstat 등)은 마법사와 같은 이름표로
    return c ? t('monitoring:status.customLabel', { name: c.query_name || t('monitoring:status.unnamed') }) : t(`monitoring:wiz.group.${groupId}`, { defaultValue: groupId });
  };

  const currentIsBuiltin = selectedQueryId && builtinQueries.some(q => q.query_id === selectedQueryId);
  const currentIsCustom = selectedQueryId && (selectedQueryId === NEW_CUSTOM_KEY || customQueries.some(q => q.query_id === selectedQueryId));

  // builtin INSERT SQL — 자동 생성. 사용자가 편집할 수 없으나 표시용으로 보여줌.
  // collector 의 _insertMetricsToExternalDS 가 실제로 사용하는 SQL 과 동일.
  const BUILTIN_AUTO_INSERT_SQL = `INSERT INTO mon_metrics (conn_id, metric_group, metric_name, collected_at, value, text_value, labels)
VALUES (:connId, :metricGroup, :metricName, :collectedAt, :value, :textValue, :labels)`;

  const currentDef = (() => {
    if (!selectedQueryId) return null;
    if (selectedQueryId === NEW_CUSTOM_KEY) return null;
    if (currentIsBuiltin) {
      const base = builtinQueries.find(q => q.query_id === selectedQueryId);
      if (selectedInstanceId) {
        const ov = queryOverrides.find(o => o.query_id === selectedQueryId);
        if (ov) {
          return {
            ...base,
            select_sql: ov.select_sql || base?.select_sql || ''
            // builtin 의 insert_sql 은 자동 생성이라 override 무의미
          };
        }
      }
      return base || null;
    }
    return customQueries.find(q => q.query_id === selectedQueryId) || null;
  })();

  const flashMessage = useCallback((type, text, ms = 3000) => {
    setMessage({ type, text });
    if (ms) setTimeout(() => setMessage(null), ms);
  }, []);

  const loadInstances = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/instances`);
      if (!res.ok) throw new Error('Failed to load instances');
      const data = await res.json();
      setInstances(data.data || []);
    } catch (err) {
      flashMessage('error', err.message);
    }
  }, [API_BASE, flashMessage]);

  const loadDatasources = useCallback(async () => {
    try {
      // 저장할 곳 목록 — 수집 저장소 + 연결(2026-10-18, 데이터소스를 따로 두지 않는다)
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/sources`);
      if (!res.ok) throw new Error('Failed to load sources');
      const data = await res.json();
      setDatasources(data.data || []);
    } catch (err) {
      flashMessage('error', err.message);
    }
  }, [API_BASE, flashMessage]);


  const loadBuiltins = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/criteria/queries`);
      if (!res.ok) throw new Error('Failed to load queries');
      const data = await res.json();
      setBuiltinQueries(data.data || []);
    } catch (err) {
      flashMessage('error', err.message);
    }
  }, [API_BASE, flashMessage]);

  const loadCustoms = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/criteria/custom-queries`);
      if (!res.ok) throw new Error('Failed to load custom queries');
      const data = await res.json();
      setCustomQueries(data.data || []);
    } catch (err) {
      flashMessage('error', err.message);
    }
  }, [API_BASE, flashMessage]);

  const loadOverrides = useCallback(async (instanceId) => {
    if (!instanceId) { setQueryOverrides([]); return; }
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/criteria/instances/${encodeURIComponent(instanceId)}/queries`);
      if (!res.ok) throw new Error('Failed to load overrides');
      const data = await res.json();
      setQueryOverrides(data.data || []);
    } catch (err) {
      flashMessage('error', err.message);
    }
  }, [API_BASE, flashMessage]);

  const loadMappings = useCallback(async (instanceId, queryId) => {
    try {
      const url = instanceId
        ? `${API_BASE}/sql-developer/monitoring/criteria/instances/${encodeURIComponent(instanceId)}/queries/${queryId}/mappings`
        : `${API_BASE}/sql-developer/monitoring/criteria/queries/${queryId}/mappings`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to load mappings');
      const data = await res.json();
      setMappings(data.data || []);
    } catch (err) {
      flashMessage('error', err.message);
    }
  }, [API_BASE, flashMessage]);

  const loadGroupConfigs = useCallback(async (instanceId) => {
    if (!instanceId) { setGroupConfigs([]); return; }
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/instances/${encodeURIComponent(instanceId)}/config`);
      if (!res.ok) throw new Error('Failed to load group configs');
      const data = await res.json();
      setGroupConfigs(data.data || []);
    } catch (err) {
      flashMessage('error', err.message);
    }
  }, [API_BASE, flashMessage]);

  const loadHistory = useCallback(async (instanceId, groupId, limit = 20) => {
    if (!instanceId || !groupId) return;
    setHistoryMap(prev => ({ ...prev, [groupId]: { ...(prev[groupId] || {}), loading: true } }));
    try {
      const url = `${API_BASE}/sql-developer/monitoring/instances/${encodeURIComponent(instanceId)}/groups/${encodeURIComponent(groupId)}/history?limit=${limit}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(t('monitoring:history.loadFailed', { status: res.status }));
      const json = await res.json();
      const d = json.data || {};
      setHistoryMap(prev => ({
        ...prev,
        [groupId]: {
          summary: d.summary || {},
          items: d.items || [],
          columns: d.columns || [],
          kind: d.kind || 'unknown',
          targetTableName: d.targetTableName || null,
          note: d.note || null,
          error: d.error || null,
          loaded: true,
          loading: false
        }
      }));
    } catch (err) {
      setHistoryMap(prev => ({ ...prev, [groupId]: { ...(prev[groupId] || {}), loaded: true, loading: false, error: err.message } }));
    }
  }, [API_BASE]);

  useEffect(() => {
    if (isOpen) {
      loadInstances();
      loadDatasources();
      loadBuiltins();
      loadCustoms();
      setSelectedQueryId(prev => prev || 'sessions');
    }
  }, [isOpen, loadInstances, loadDatasources, loadBuiltins, loadCustoms]);


  useEffect(() => {
    if (!isOpen) return;
    if (tab === 'queries') {
      loadOverrides(selectedInstanceId);
    } else if (tab === 'mappings') {
      // 선택된 수집 기준이 builtin 일 때만 매핑 로드 (custom 은 매핑 불필요)
      const isBuiltin = !!(selectedQueryId && groupLabels[selectedQueryId]);
      if (isBuiltin) {
        loadMappings(selectedInstanceId, selectedQueryId);
      }
    } else if (tab === 'schedule') {
      loadGroupConfigs(selectedInstanceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tab, selectedInstanceId, selectedQueryId, loadOverrides, loadMappings, loadGroupConfigs]);


  // 스케줄 탭 진입 시 각 그룹의 최근 이력(요약+최근 N건)을 가져와 summary 표시
  useEffect(() => {
    if (!isOpen || tab !== 'schedule' || !selectedInstanceId) return;
    setHistoryMap({});
    setExpandedGroups(new Set());
    (groupConfigs || []).forEach(cfg => loadHistory(selectedInstanceId, cfg.groupId, 20));
  }, [isOpen, tab, selectedInstanceId, groupConfigs, loadHistory]);

  const toggleExpand = (groupId) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      return next;
    });
  };

  const formatRelative = (isoOrDate) => {
    if (!isoOrDate) return '—';
    try {
      // 지역 변수 이름을 t 로 두면 번역 함수 t 를 가려 늘 예외 → 원문 시각으로 떨어졌다(2026-10-28)
      const ms = new Date(isoOrDate).getTime();
      if (!ms) return String(isoOrDate);
      const diffSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
      if (diffSec < 60) return t('monitoring:ago.seconds', { n: diffSec });
      if (diffSec < 3600) return t('monitoring:ago.minutes', { n: Math.floor(diffSec / 60) });
      if (diffSec < 86400) return t('monitoring:ago.hours', { n: Math.floor(diffSec / 3600) });
      return t('monitoring:ago.days', { n: Math.floor(diffSec / 86400) });
    } catch {
      return String(isoOrDate);
    }
  };

  const groupKindOf = (groupId) => {
    if (groupLabels[groupId]) return 'builtin';
    if (customQueries.some(q => q.query_id === groupId)) return 'custom';
    return 'unknown';
  };

  const selectQuery = (queryId) => {
    setSelectedQueryId(queryId);
    setEditDraft(null);
    setIsEditing(false);
  };

  const startNewCustom = () => {
    setSelectedQueryId(NEW_CUSTOM_KEY);
    // 신규 사용자 수집 기준의 수집 대상 — 지금 고른 대상, 없으면 첫 등록 대상(2026-10-18 — 데이터소스를 거치지 않는다)
    const defaultTargetInstance = selectedInstanceId || ((instances.find(i => !i.viewOnly) || instances[0] || {}).instanceId) || '';
    setEditDraft({
      query_name: '',
      select_sql: '',
      insert_sql: '',
      target_instance_id: defaultTargetInstance || '',
      target_table_name: '',
      description: ''
    });
    setIsEditing(true);
  };

  // 쿼리 템플릿에서 「수집 기준으로 등록」(2026-10-28) — 새 사용자 기준 초안을 템플릿의 이름·SQL 로 채운다. 저장은 사람이(적재 대상·INSERT 를 정해야 한다)
  const seedNonce = seed ? seed.nonce : null;
  useEffect(() => {
    if (!isOpen || !seed) return;
    setTab('queries');
    setSelectedInstanceId(null);
    setSelectedQueryId(NEW_CUSTOM_KEY);
    setEditDraft({
      query_name: seed.name || '',
      select_sql: seed.sql || '',
      insert_sql: '',
      target_instance_id: 'conn_builtin_monitoring',
      target_table_name: '',
      description: seed.description || ''
    });
    setIsEditing(true);
    flashMessage('info', t('monitoring:crit2.seeded', { name: seed.name || '' }), 6000);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 새 요청(nonce)에만
  }, [isOpen, seedNonce]);

  const handleStartEdit = () => {
    if (!currentDef) return;
    setEditDraft({ ...currentDef });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditDraft(null);
    setIsEditing(false);
    if (selectedQueryId === NEW_CUSTOM_KEY) {
      setSelectedQueryId(builtinQueries[0]?.query_id || null);
    }
  };

  const handleSaveBuiltin = async () => {
    if (!editDraft || !selectedQueryId) return;
    try {
      setLoading(true);
      // builtin: insertSql 은 자동 생성이라 보내지 않음. targetDataSourceId 만 추가.
      const body = {
        queryName: editDraft.query_name,
        selectSql: editDraft.select_sql,
        targetDataSourceId: editDraft.target_data_source_id || 'ds_monitoring_db',
        description: editDraft.description
      };
      const url = selectedInstanceId
        ? `${API_BASE}/sql-developer/monitoring/criteria/instances/${encodeURIComponent(selectedInstanceId)}/queries/${selectedQueryId}`
        : `${API_BASE}/sql-developer/monitoring/criteria/queries/${selectedQueryId}`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('Failed to save query');
      flashMessage('success', selectedInstanceId ? t('monitoring:toast.overrideSaved') : t('monitoring:toast.builtinSaved'));
      await loadBuiltins();
      if (selectedInstanceId) await loadOverrides(selectedInstanceId);
      setIsEditing(false);
      setEditDraft(null);
    } catch (err) {
      flashMessage('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCustom = async () => {
    if (!editDraft) return;
    const { query_name, select_sql, insert_sql, target_instance_id, target_table_name } = editDraft;
    if (!query_name || !select_sql || !insert_sql || !target_instance_id || !target_table_name) {
      flashMessage('error', t('monitoring:toast.requiredFields'));
      return;
    }
    try {
      setLoading(true);
      const isNew = selectedQueryId === NEW_CUSTOM_KEY;
      const url = isNew
        ? `${API_BASE}/sql-developer/monitoring/criteria/custom-queries`
        : `${API_BASE}/sql-developer/monitoring/criteria/custom-queries/${encodeURIComponent(selectedQueryId)}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          queryName: query_name,
          selectSql: select_sql,
          insertSql: insert_sql,
          targetInstanceId: target_instance_id,
          targetTableName: target_table_name,
          description: editDraft.description || ''
        })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to save custom query');
      }
      const data = await res.json();
      flashMessage('success', t('monitoring:toast.customSaved'));
      await loadCustoms();
      if (isNew && data.data?.query_id) setSelectedQueryId(data.data.query_id);
      setIsEditing(false);
      setEditDraft(null);
    } catch (err) {
      flashMessage('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (selectedQueryId === NEW_CUSTOM_KEY || currentIsCustom) return handleSaveCustom();
    return handleSaveBuiltin();
  };

  const handleResetBuiltin = async () => {
    if (!selectedQueryId || !currentIsBuiltin) return;
    const ok = await confirm({
      title: t('monitoring:criteria.reset'),
      message: t('monitoring:confirm.resetBuiltin'),
      confirmText: t('monitoring:act.reset'),
      variant: 'danger',
    });
    if (!ok) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/criteria/queries/${selectedQueryId}/reset`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed to reset');
      flashMessage('success', t('monitoring:toast.resetToTemplate'));
      await loadBuiltins();
      setIsEditing(false);
      setEditDraft(null);
    } catch (err) {
      flashMessage('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustom = async () => {
    if (!selectedQueryId || !currentIsCustom || selectedQueryId === NEW_CUSTOM_KEY) return;
    const ok = await confirm({
      title: t('monitoring:criteria.delete'),
      message: t('monitoring:confirm.deleteCustom'),
      confirmText: t('monitoring:act.delete'),
      variant: 'danger',
    });
    if (!ok) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/criteria/custom-queries/${encodeURIComponent(selectedQueryId)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
      flashMessage('success', t('monitoring:toast.deleted'));
      await loadCustoms();
      setSelectedQueryId(builtinQueries[0]?.query_id || null);
      setIsEditing(false);
      setEditDraft(null);
    } catch (err) {
      flashMessage('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOverride = async () => {
    if (!selectedInstanceId || !selectedQueryId) return;
    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE}/sql-developer/monitoring/criteria/instances/${encodeURIComponent(selectedInstanceId)}/queries/${selectedQueryId}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error('Failed to delete override');
      flashMessage('success', t('monitoring:toast.overrideDeleted'));
      await loadOverrides(selectedInstanceId);
    } catch (err) {
      flashMessage('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMappings = async () => {
    // 좌측 목록에서 선택된 builtin 수집 기준 기준으로 저장
    const targetId = selectedQueryId && groupLabels[selectedQueryId] ? selectedQueryId : null;
    if (!targetId) return;
    try {
      setLoading(true);
      const url = selectedInstanceId
        ? `${API_BASE}/sql-developer/monitoring/criteria/instances/${encodeURIComponent(selectedInstanceId)}/queries/${targetId}/mappings`
        : `${API_BASE}/sql-developer/monitoring/criteria/queries/${targetId}/mappings`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mappings })
      });
      if (!res.ok) throw new Error('Failed to save mappings');
      flashMessage('success', t('monitoring:toast.mappingSaved'));
    } catch (err) {
      flashMessage('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 수집 대상(연결) 고르기는 왼쪽 목록 하나다(2026-10-28 — 예전에는 탭마다 고르기 상자가 있었다). 「전체」 = 연결과 무관한 기준 정의.
   */
  const ownerLabel = (inst) => {
    if (!inst.registered) return t('monitoring:crit.notRegistered');
    if (inst.owner && inst.owner.admin) return t('monitoring:cstat.ownerAdmin');
    return (inst.owner && (inst.owner.name || inst.owner.usrid)) || '';
  };

  // ==== 스케줄 탭 핸들러 ====
  // 처음 거는 수집은 **등록**이다(2026-10-03) — 내가 접근할 수 있는 이 대상의 연결로 주인이 된다.
  // 이미 등록된 대상이면 연결을 보내지 않는다(주인의 연결을 그대로 쓴다).
  const registerBody = () => {
    const inst = instances.find(i => i.instanceId === selectedInstanceId);
    if (!inst || inst.registered) return {};
    const usable = (inst.conns || []).find(c => c.canRegister);
    const connectionId = usable ? usable.id : (inst.connIds || [])[0];
    return connectionId ? { connectionId } : {};
  };
  // 서버가 말하는 사유(권한·이미 주인 있음 등)를 그대로 보인다
  const serverError = async (res, fallback) => {
    try { const j = await res.json(); return j.error || fallback; } catch (_e) { return fallback; }
  };
  const handleStartGroup = async (groupId) => {
    if (!selectedInstanceId) return;
    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE}/sql-developer/monitoring/instances/${encodeURIComponent(selectedInstanceId)}/groups/${groupId}/start`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(registerBody()) }
      );
      if (!res.ok) throw new Error(await serverError(res, 'Failed to start collection'));
      flashMessage('success', t('monitoring:toast.started', { name: labelFor(groupId) }));
      await loadGroupConfigs(selectedInstanceId);
    } catch (err) {
      flashMessage('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStopGroup = async (groupId) => {
    if (!selectedInstanceId) return;
    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE}/sql-developer/monitoring/instances/${encodeURIComponent(selectedInstanceId)}/groups/${groupId}/stop`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      if (!res.ok) throw new Error(await serverError(res, 'Failed to stop collection'));
      flashMessage('success', t('monitoring:toast.stopped', { name: labelFor(groupId) }));
      await loadGroupConfigs(selectedInstanceId);
    } catch (err) {
      flashMessage('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGroup = async (groupId) => {
    if (!selectedInstanceId) return;
    const cfg = groupConfigs.find(c => c.groupId === groupId);
    if (!cfg) return;
    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE}/sql-developer/monitoring/instances/${encodeURIComponent(selectedInstanceId)}/groups/${groupId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...registerBody(),
            enabled: cfg.enabled,
            intervalSec: cfg.intervalSec,
            retainDays: cfg.retainDays,
            maxMb: cfg.maxMb
          })
        }
      );
      if (!res.ok) throw new Error(await serverError(res, 'Failed to save group config'));
      flashMessage('success', t('monitoring:toast.settingsSaved', { name: labelFor(groupId) }));
      setEditingGroupId(null);
    } catch (err) {
      flashMessage('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = (groupId, field, value) => {
    setGroupConfigs(prev =>
      prev.map(cfg => cfg.groupId === groupId ? { ...cfg, [field]: value } : cfg)
    );
  };

  const handleAddMapping = () => {
    if (!newMapping.columnAlias || !newMapping.fieldName) return;
    setMappings([...mappings, {
      id: (mappings.length > 0 ? Math.max(...mappings.map(m => m.id || 0)) + 1 : 1),
      column_alias: newMapping.columnAlias,
      field_name: newMapping.fieldName,
      data_type: newMapping.dataType
    }]);
    setNewMapping({ columnAlias: '', fieldName: '', dataType: 'real' });
  };

  const handleDeleteMapping = (id) => setMappings(mappings.filter(m => m.id !== id));

  // 창처럼 — 끌어 이동 · 최대화 · 새 창으로 분리(다른 모니터에서 전체 화면). common/useModalWindow (2026-10-06)
  const win = useModalWindow({ title: t('monitoring:criteria.settings') });
  const { reset: resetWin } = win;
  useEffect(() => { if (!isOpen) resetWin(); }, [isOpen, resetWin]);   // 닫으면 다음엔 제자리·앱 안에서

  if (!isOpen) return null;

  const displayView = isEditing && editDraft ? editDraft : currentDef;

  return win.wrap(
    <div className="criteria-modal-overlay" style={win.overlayStyle}>
      <div className="app-modal criteria-modal" data-app-modal ref={win.frameRef} style={win.frameStyle}>
        <div className="app-modal-header criteria-modal-header" {...win.headerProps}>
          <h2 className="app-modal-title"><FiSettings size={14} />{t('monitoring:criteria.settings')}</h2>
          <span className="app-modal-header-right">
          {win.controls}
          <button className="app-modal-close criteria-modal-close" onClick={onClose}>
            <FiX size={16} />
          </button>
          </span>
        </div>

        {/* 알림 토스트 오버레이 — 레이아웃을 차지하지 않으며 아래 콘텐츠 위에 떠서 표시됨 */}
        <div className="criteria-message-overlay" aria-live="polite">
          {message && (
            <div className={`criteria-message criteria-message-${message.type}`}>
              {message.text}
            </div>
          )}
        </div>

        <div className="criteria-modal-body criteria-body-split">
          {/* ===== 왼쪽 — 수집 대상(연결) 목록(2026-10-28 사용자 요청). 「전체」 = 연결과 무관한 기준 정의, 연결 = 그 연결의 덮어쓰기·수집 설정·현황 ===== */}
          <aside className="criteria-sidebar">
            <div className="criteria-group-label">{t('monitoring:crit2.connections')}</div>
            <button
              type="button"
              className={`criteria-query-item criteria-conn-item ${!selectedInstanceId ? 'active' : ''}`}
              onClick={() => setSelectedInstanceId(null)}
              disabled={loading || isEditing}
            >
              <span className="criteria-conn-name">{t('monitoring:crit2.allConns')}</span>
              <span className="criteria-conn-sub">{t('monitoring:crit2.allConnsSub')}</span>
            </button>
            {instances.map(inst => (
              <button
                type="button"
                key={inst.instanceId}
                className={`criteria-query-item criteria-conn-item ${selectedInstanceId === inst.instanceId ? 'active' : ''}`}
                onClick={() => setSelectedInstanceId(inst.instanceId)}
                disabled={loading || isEditing}
                title={`${inst.instanceId} (${ownerLabel(inst)})`}
              >
                <span className="criteria-conn-name">{inst.displayName || inst.instanceId}</span>
                <span className={`criteria-conn-sub ${inst.collecting > 0 ? 'on' : ''}`}>
                  {inst.collecting > 0 ? t('monitoring:crit2.collecting', { n: inst.collecting })
                    : inst.registered ? t('monitoring:crit2.stopped') : t('monitoring:crit2.notRegistered')}
                </span>
              </button>
            ))}
            {instances.length === 0 && <div className="criteria-empty-hint">{t('monitoring:crit2.noConns')}</div>}
          </aside>

          {/* ===== 우측 — 내부 탭 패널 ===== */}
          <section className="criteria-workpane">
            <div className="criteria-modal-tabs criteria-inner-tabs" role="tablist">
              {/* 오라클 성능 분석과 같은 탭 줄 — 왼쪽 정렬·아이콘·밑줄(2026-10-03) */}
              {/* 순서 = 기준 정의 → 수집 설정(기준 ↔ 연결) → 스케줄·현황(2026-10-28 사용자 요청) */}
              <button type="button" role="tab" aria-selected={tab === 'queries'} className={`criteria-tab ${tab === 'queries' ? 'active' : ''}`} onClick={() => setTab('queries')}><FiCode size={13} /> {t('monitoring:crit2.tabDefine')}</button>
              <button type="button" role="tab" aria-selected={tab === 'mappings'} className={`criteria-tab ${tab === 'mappings' ? 'active' : ''}`} onClick={() => setTab('mappings')}><FiShuffle size={13} /> {t('monitoring:criteria.mapping')}</button>
              <button type="button" role="tab" aria-selected={tab === 'schedule'} className={`criteria-tab ${tab === 'schedule' ? 'active' : ''}`} onClick={() => setTab('schedule')}><FiClock size={13} /> {t('monitoring:crit2.tabSchedule')}</button>
              <button type="button" role="tab" aria-selected={tab === 'status'} className={`criteria-tab ${tab === 'status' ? 'active' : ''}`} onClick={() => setTab('status')}><FiActivity size={13} /> {t('monitoring:cstat.tab')}</button>
            </div>

            {/* 수집 기준 목록 — 쿼리·매핑 탭에서 위쪽 가로 줄로(2026-10-28 사용자 요청: 왼쪽은 연결 목록이다) */}
            {(tab === 'queries' || tab === 'mappings') && (
              <div className="criteria-topbar">
                <span className="criteria-topbar-label">{t('monitoring:criteria.builtin')}</span>
                {builtinQueries.map(q => (
                  <button
                    type="button"
                    key={q.query_id}
                    className={`criteria-chip ${selectedQueryId === q.query_id ? 'active' : ''}`}
                    onClick={() => selectQuery(q.query_id)}
                    disabled={loading}
                    title={selectedInstanceId && queryOverrides.some(o => o.query_id === q.query_id) ? t('monitoring:crit.applyInstanceOverride') : undefined}
                  >
                    {q.query_name}
                    {selectedInstanceId && queryOverrides.some(o => o.query_id === q.query_id) && <FiStar size={10} className="criteria-chip-star" />}
                  </button>
                ))}
                <span className="criteria-topbar-sep" />
                <span className="criteria-topbar-label">{t('monitoring:criteria.custom')}</span>
                {customQueries.map(q => (
                  <button
                    type="button"
                    key={q.query_id}
                    className={`criteria-chip ${selectedQueryId === q.query_id ? 'active' : ''}`}
                    onClick={() => selectQuery(q.query_id)}
                    disabled={loading}
                  >{q.query_name || t('monitoring:status.unnamed')}</button>
                ))}
                {selectedQueryId === NEW_CUSTOM_KEY && <span className="criteria-chip active">{t('monitoring:status.newCustom')}</span>}
                {customQueries.length === 0 && selectedQueryId !== NEW_CUSTOM_KEY && <span className="criteria-topbar-empty">{t('monitoring:criteria.noCustom')}</span>}
                <button type="button" className="criteria-btn-icon" onClick={startNewCustom} disabled={loading} title={t('monitoring:criteria.addCustom')}>
                  <FiPlus size={14} />
                </button>
              </div>
            )}
            {(tab === 'queries' || tab === 'mappings') && (
              <div className={`criteria-scope ${selectedInstanceId && currentIsBuiltin ? 'conn' : ''}`}>
                {currentIsBuiltin && selectedInstanceId
                  ? t('monitoring:crit2.scopeConn', { name: (instances.find(i => i.instanceId === selectedInstanceId) || {}).displayName || selectedInstanceId })
                  : t('monitoring:crit2.scopeAll')}
              </div>
            )}

            <div className="criteria-workpane-body">
              {tab === 'status' && (
                <CollectionStatusPanel apiBase={API_BASE} labelFor={labelFor} onMessage={flashMessage} instanceId={selectedInstanceId} onChanged={loadInstances} />
              )}

          {tab === 'queries' && (
            <>
              <details className="criteria-tab-intro">
                <summary>
                  <strong>{t('monitoring:help.queryTitle')}</strong> {t('monitoring:help.queryTab')} <em className="criteria-intro-more">{t('monitoring:help.clickForDetail')}</em>
                </summary>
                <div className="criteria-intro-body">
                  <ul>
                    <li><strong>{t('monitoring:criteria.builtinFull')}</strong>{t('monitoring:help.builtinDesc')}</li>
                    <li><strong>{t('monitoring:criteria.customFull')}</strong>{t('monitoring:help.customDesc')}</li>
                    <li><strong>{t('monitoring:crit.targetBuiltin')}</strong>{t('monitoring:help.targetDesc')}</li>
                    <li><strong>{t('monitoring:crit.instanceOverride')}</strong>{t('monitoring:help.overrideDesc')}</li>
                    <li>{t('monitoring:help.firstColumn')} <code>collected_at</code> {t('monitoring:help.dbTimeInjected')}</li>
                  </ul>
                </div>
              </details>

              <div className="criteria-query-section criteria-query-section-noleft">

                {(displayView || currentIsCustom || selectedQueryId === NEW_CUSTOM_KEY) && (
                  <div className="criteria-query-editor">
                    {(currentIsCustom || selectedQueryId === NEW_CUSTOM_KEY) && (
                      <>
                        <div className="criteria-field-row">
                          <label className="criteria-label">{t('monitoring:crit.name')}</label>
                          <input
                            type="text"
                            className="criteria-text-input"
                            value={displayView?.query_name || ''}
                            onChange={(e) => isEditing && setEditDraft({ ...editDraft, query_name: e.target.value })}
                            disabled={!isEditing || loading}
                            placeholder={t('monitoring:critPh.name')}
                          />
                        </div>
                        <div className="criteria-field-row criteria-field-inline">
                          <div style={{ flex: 1 }}>
                            {/* 적재 대상 — INSERT 가 도는 곳(2026-10-28 사용자 요청 「다른 DB 연결도」). 내장 저장소(usr_ 표만) · 내 연결 ·
                                (옛 값) 수집 대상 주소. 예전에는 수집할 수 있는 대상(Oracle·PostgreSQL)만 골라졌다 */}
                            <label className="criteria-label">{t('monitoring:src.loadTarget')}</label>
                            <select
                              className="criteria-select"
                              value={displayView?.target_instance_id || ''}
                              onChange={(e) => { if (isEditing) setEditDraft({ ...editDraft, target_instance_id: e.target.value }); }}
                              disabled={!isEditing || loading}
                            >
                              <option value="">{t('monitoring:act.select3')}</option>
                              <optgroup label={t('monitoring:src.store')}>
                                <option value="conn_builtin_monitoring">{t('monitoring:src.storeUsr')}</option>
                              </optgroup>
                              <optgroup label={t('monitoring:src.connections')}>
                                {datasources.filter(ds => ds.kind === 'connection').map(ds => (
                                  <option key={ds.id} value={ds.id}>{sourceLabel(ds, t)}</option>
                                ))}
                              </optgroup>
                              {(() => {
                                const known = new Set(['conn_builtin_monitoring', ...datasources.map(ds => ds.id)]);
                                const legacy = [...instances.filter(i => !i.viewOnly && !known.has(i.instanceId)),
                                  ...(displayView?.target_instance_id && !known.has(displayView.target_instance_id) && !instances.some(i => i.instanceId === displayView.target_instance_id)
                                    ? [{ instanceId: displayView.target_instance_id, displayName: displayView.target_instance_id }] : [])];
                                return legacy.length > 0 ? (
                                  <optgroup label={t('monitoring:src.instances')}>
                                    {legacy.map(inst => (
                                      <option key={inst.instanceId} value={inst.instanceId}>
                                        {inst.displayName || inst.instanceId}{inst.displayName && inst.displayName !== inst.instanceId ? ` — ${inst.instanceId}` : ''}
                                      </option>
                                    ))}
                                  </optgroup>
                                ) : null;
                              })()}
                            </select>
                            {displayView?.target_instance_id === 'conn_builtin_monitoring' && (
                              <div className="criteria-hint">{t('monitoring:src.storeUsrHint')}</div>
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <label className="criteria-label">{t('monitoring:crit.targetTable')}</label>
                            <input
                              type="text"
                              className="criteria-text-input"
                              value={displayView?.target_table_name || ''}
                              onChange={(e) => isEditing && setEditDraft({ ...editDraft, target_table_name: e.target.value })}
                              disabled={!isEditing || loading}
                              placeholder={t('monitoring:critPh.table')}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* builtin: 수집 저장소 — 내장(기본) 또는 연결(그 DB 의 mon_metrics 에 같은 형식으로, 2026-10-18) */}
                    {currentIsBuiltin && (
                      <div className="criteria-field-row">
                        <label className="criteria-label">
                          {t('monitoring:src.saveTo')}
                          <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--color-text-muted, #999)' }}>
                            {t('monitoring:src.saveHint')}
                          </span>
                        </label>
                        <select
                          className="criteria-select"
                          value={displayView?.target_data_source_id || STORE_ID}
                          onChange={(e) => isEditing && setEditDraft({ ...editDraft, target_data_source_id: e.target.value })}
                          disabled={!isEditing || loading}
                        >
                          {withCurrent(datasources, displayView?.target_data_source_id, t).map(ds => (
                            <option key={ds.id} value={ds.id}>{ds.kind === 'missing' ? ds.name : sourceLabel(ds, t)}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="criteria-field-row">
                      <label className="criteria-label">{t('monitoring:criteria.selectQuery')}</label>
                      <textarea
                        className="criteria-query-textarea select-area"
                        value={displayView?.select_sql || displayView?.query_sql || ''}
                        onChange={(e) => isEditing && setEditDraft({ ...editDraft, select_sql: e.target.value })}
                        placeholder={t('monitoring:help.selectHint')}
                        disabled={!isEditing || loading}
                      />
                    </div>
                    <div className="criteria-field-row">
                      <label className="criteria-label">
                        {t('monitoring:crit.insertQuery')}
                        {currentIsBuiltin && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--color-text-muted, #999)' }}>
                            {t('monitoring:crit.insertAutoNote')}
                          </span>
                        )}
                      </label>
                      <textarea
                        className="criteria-query-textarea insert-area"
                        value={
                          currentIsBuiltin
                            ? BUILTIN_AUTO_INSERT_SQL
                            : (displayView?.insert_sql || '')
                        }
                        onChange={(e) => {
                          if (!isEditing || currentIsBuiltin) return;
                          setEditDraft({ ...editDraft, insert_sql: e.target.value });
                        }}
                        placeholder={
                          currentIsCustom || selectedQueryId === NEW_CUSTOM_KEY
                            ? t('monitoring:help.insertHint')
                            : ""
                        }
                        readOnly={currentIsBuiltin}
                        disabled={!currentIsBuiltin && (!isEditing || loading)}
                        style={currentIsBuiltin ? { fontFamily: 'var(--font-mono, Consolas, monospace)', background: 'var(--color-bg-secondary, #f5f5f5)', cursor: 'not-allowed' } : undefined}
                      />
                    </div>

                    <div className="criteria-query-actions">
                      {isEditing ? (
                        <>
                          <button
                            className="criteria-btn criteria-btn-primary"
                            onClick={handleSave}
                            disabled={loading}
                          >{t('monitoring:act.save')}</button>
                          <button
                            className="criteria-btn criteria-btn-secondary"
                            onClick={handleCancelEdit}
                            disabled={loading}
                          >{t('monitoring:act.cancel')}</button>
                        </>
                      ) : (
                        <>
                          <button
                            className="criteria-btn criteria-btn-primary"
                            onClick={handleStartEdit}
                            disabled={loading}
                          >{t('monitoring:act.edit')}</button>
                          {currentIsBuiltin && !selectedInstanceId && (
                            <button
                              className="criteria-btn criteria-btn-secondary"
                              onClick={handleResetBuiltin}
                              disabled={loading}
                              title={t('monitoring:act.restoreTemplate')}
                            >
                              <FiRotateCcw size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                              {t('monitoring:act.reset')}
                            </button>
                          )}
                          {currentIsBuiltin && selectedInstanceId && queryOverrides.some(o => o.query_id === selectedQueryId) && (
                            <button
                              className="criteria-btn criteria-btn-danger"
                              onClick={handleDeleteOverride}
                              disabled={loading}
                            >{t('monitoring:act.resetGlobal')}</button>
                          )}
                          {currentIsCustom && selectedQueryId !== NEW_CUSTOM_KEY && (
                            <button
                              className="criteria-btn criteria-btn-danger"
                              onClick={handleDeleteCustom}
                              disabled={loading}
                            >
                              <FiTrash2 size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                              {t('monitoring:act.delete')}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {tab === 'mappings' && (
            <div className="criteria-mapping-section">
              <details className="criteria-tab-intro">
                <summary>
                  <strong>{t('monitoring:help.mappingTitle')}</strong> {t('monitoring:help.mappingPre')} <code>mon_metrics</code> {t('monitoring:help.mappingPost')} <em className="criteria-intro-more">{t('monitoring:help.clickForDetail')}</em>
                </summary>
                <div className="criteria-intro-body">
                  <ul>
                    <li><strong>role = value</strong> {t('monitoring:help.kindMetric')}</li>
                    <li><strong>role = label</strong> {t('monitoring:help.kindLabel')} <code>labels</code> {t('monitoring:help.kindLabelJson')}</li>
                    <li><strong>role = counter</strong> {t('monitoring:help.kindCounter')}</li>
                    <li><strong>role = text_value</strong> {t('monitoring:help.kindText')}</li>
                    <li><strong>role = time</strong>{t('monitoring:help.collectedAtDesc')}</li>
                  </ul>
                  <div style={{ marginTop: 6 }}>
                    {t('monitoring:crit.mappingScope1')}
                  </div>
                </div>
              </details>
              {!currentIsBuiltin && (
                <p className="criteria-empty-hint" style={{ marginTop: 0 }}>
                  {t('monitoring:crit.mappingScope2')}
                  {(!selectedQueryId || selectedQueryId === NEW_CUSTOM_KEY) && t('monitoring:criteria.pickLeftMapping')}
                </p>
              )}
              {currentIsBuiltin && (
                <>
                  <div className="criteria-empty-hint" style={{ marginTop: 0 }}>
                    {t('monitoring:crit.mappingTarget')}: <strong>{groupLabels[selectedQueryId]}</strong>
                  </div>

                  <table className="criteria-mapping-table">
                <thead>
                  <tr>
                    <th>{t('monitoring:crit.queryColumn')}</th>
                    <th>{t('monitoring:crit.storedFieldName')}</th>
                    <th>{t('monitoring:crit.type')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map(m => (
                    <tr key={m.id || `${m.column_alias}-${m.field_name}`}>
                      <td>{m.column_alias}</td>
                      <td>{m.field_name}</td>
                      <td>{m.data_type}</td>
                      <td>
                        <button className="criteria-btn-delete" onClick={() => handleDeleteMapping(m.id)} disabled={loading}>
                          {t('monitoring:act.delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="criteria-mapping-new">
                    <td>
                      <input
                        type="text" placeholder={t('monitoring:crit.columnName')}
                        value={newMapping.columnAlias}
                        onChange={(e) => setNewMapping({ ...newMapping, columnAlias: e.target.value })}
                        disabled={loading}
                      />
                    </td>
                    <td>
                      <input
                        type="text" placeholder={t('monitoring:crit.fieldName')}
                        value={newMapping.fieldName}
                        onChange={(e) => setNewMapping({ ...newMapping, fieldName: e.target.value })}
                        disabled={loading}
                      />
                    </td>
                    <td>
                      <select
                        className="criteria-type-select"
                        value={newMapping.dataType}
                        onChange={(e) => setNewMapping({ ...newMapping, dataType: e.target.value })}
                        disabled={loading}
                      >
                        <option value="real">real</option>
                        <option value="integer">integer</option>
                        <option value="text">text</option>
                      </select>
                    </td>
                    <td>
                      <button
                        className="criteria-btn-add"
                        onClick={handleAddMapping}
                        disabled={loading || !newMapping.columnAlias || !newMapping.fieldName}
                      >{t('monitoring:act.add')}</button>
                    </td>
                  </tr>
                </tbody>
              </table>

                  <div className="criteria-mapping-actions">
                    <button
                      className="criteria-btn criteria-btn-primary"
                      onClick={handleSaveMappings}
                      disabled={loading}
                    >{t('monitoring:act.save')}</button>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === 'schedule' && (() => {
            // 고른 연결에 건 수집 기준 전부 — 기준마다 켜기·주기·보존·이력(2026-10-28: 기준 정의와 떼어 「연결 ↔ 기준」 설정으로)
            // 기준 한 줄 — 기준 · 종류 · 상태 · 주기 · 보존 · 마지막 수집 · 동작, 이력은 펼치면 아래 줄에(2026-10-28: 카드는 기준이 많으면 길었다)
            const renderRow = (scopedId) => {
              const cfg = groupConfigs.find(c => c.groupId === scopedId);
              const hist = historyMap[scopedId];
              const kind = groupKindOf(scopedId);
              const label = kind === 'builtin'
                ? (groupLabels[scopedId] || scopedId)
                : (customQueries.find(q => q.query_id === scopedId)?.query_name || labelFor(scopedId));
              const editing = editingGroupId === scopedId;
              const isExpanded = expandedGroups.has(scopedId);
              return (
                <React.Fragment key={scopedId}>
                  <tr className={cfg.enabled ? 'on' : ''}>
                    <td>
                      <span className="criteria-group-name">{label}</span>{' '}
                      <span className={`criteria-type-badge criteria-type-${kind}`}>
                        {kind === 'builtin' ? t('monitoring:act.default') : kind === 'custom' ? t('monitoring:act.user') : '?'}
                      </span>
                    </td>
                    <td>
                      <span className={`criteria-status-badge ${cfg.enabled ? 'active' : 'inactive'}`}>
                        {cfg.enabled ? t('monitoring:status.collecting') : t('monitoring:status.stopped')}
                      </span>
                    </td>
                    <td>
                      {editing ? (
                        <select className="criteria-select" value={cfg.intervalSec} onChange={(e) => handleUpdateConfig(scopedId, 'intervalSec', parseInt(e.target.value, 10))} disabled={loading}>
                          <option value="10">{t('monitoring:every.sec10')}</option>
                          <option value="30">{t('monitoring:every.sec30')}</option>
                          <option value="60">{t('monitoring:every.min1')}</option>
                          <option value="300">{t('monitoring:every.min5')}</option>
                        </select>
                      ) : t('monitoring:crit.seconds', { n: cfg.intervalSec })}
                    </td>
                    <td>
                      {editing ? (
                        <select className="criteria-select" value={cfg.retainDays} onChange={(e) => handleUpdateConfig(scopedId, 'retainDays', parseInt(e.target.value, 10))} disabled={loading}>
                          <option value="1">{t('monitoring:every.day1')}</option>
                          <option value="7">{t('monitoring:every.day7')}</option>
                          <option value="30">{t('monitoring:every.day30')}</option>
                          <option value="90">{t('monitoring:every.day90')}</option>
                        </select>
                      ) : t('monitoring:crit.days', { n: cfg.retainDays })}
                    </td>
                    <td title={t('monitoring:crit.histSummary', { last1h: hist?.summary?.last1hCount ?? 0, total: hist?.summary?.totalCount ?? 0 })}>
                      {formatRelative(hist?.summary?.lastCollectedAt)}
                    </td>
                    <td className="criteria-sched-actions">
                      {editing ? (
                        <>
                          <button className="criteria-icon-act primary" onClick={() => handleSaveGroup(scopedId)} disabled={loading} title={t('monitoring:act.save')} aria-label={t('monitoring:act.save')}><FiCheck size={14} /></button>
                          <button className="criteria-icon-act" onClick={() => { setEditingGroupId(null); loadGroupConfigs(selectedInstanceId); }} disabled={loading} title={t('monitoring:act.cancel')} aria-label={t('monitoring:act.cancel')}><FiX size={14} /></button>
                        </>
                      ) : (
                        <>
                          {cfg.enabled ? (
                            <button className="criteria-icon-act danger" onClick={() => handleStopGroup(scopedId)} disabled={loading} title={t('monitoring:act.stop')} aria-label={t('monitoring:act.stop')}><FiSquare size={13} /></button>
                          ) : (
                            <button className="criteria-icon-act primary" onClick={() => handleStartGroup(scopedId)} disabled={loading} title={t('monitoring:act.start')} aria-label={t('monitoring:act.start')}><FiPlay size={13} /></button>
                          )}
                          <button className="criteria-icon-act" onClick={() => setEditingGroupId(scopedId)} disabled={loading} title={t('monitoring:act.edit')} aria-label={t('monitoring:act.edit')}><FiEdit2 size={13} /></button>
                          <button className={`criteria-icon-act ${isExpanded ? 'on' : ''}`} onClick={() => toggleExpand(scopedId)}
                            title={isExpanded ? t('monitoring:history.collapse') : t('monitoring:history.expand')} aria-label={isExpanded ? t('monitoring:history.collapse') : t('monitoring:history.expand')}>
                            {isExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="criteria-sched-detail">
                      <td colSpan={6}>
                        <div className="criteria-history-detail">
                          <div className="criteria-history-head">
                            <span>{t('monitoring:history.title')} · {t('monitoring:crit.histSummary', { last1h: hist?.summary?.last1hCount ?? 0, total: hist?.summary?.totalCount ?? 0 })}</span>
                            <button className="criteria-btn-small criteria-btn-secondary" onClick={() => loadHistory(selectedInstanceId, scopedId, 20)} disabled={hist?.loading}>
                              {hist?.loading ? t('monitoring:act.loading') : t('monitoring:act.refresh')}
                            </button>
                          </div>
                          {hist?.note && <div className="criteria-empty-hint">{hist.note}</div>}
                          {hist?.error && <div className="criteria-empty-hint" style={{ color: '#b91c1c' }}>{t('monitoring:crit.errorMsg', { message: hist.error })}</div>}
                          {hist?.items && hist.items.length > 0 ? (
                            <div className="criteria-history-tablewrap">
                              <table className="criteria-history-table">
                                <thead>
                                  <tr>
                                    {(hist.columns && hist.columns.length > 0 ? hist.columns : Object.keys(hist.items[0] || {})).map(c => <th key={c}>{c}</th>)}
                                  </tr>
                                </thead>
                                <tbody>
                                  {hist.items.map((row, idx) => (
                                    <tr key={idx}>
                                      {(hist.columns && hist.columns.length > 0 ? hist.columns : Object.keys(row)).map(c => <td key={c}>{row[c] != null ? String(row[c]) : ''}</td>)}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            !hist?.loading && <div className="criteria-empty-hint">{t('monitoring:history.empty')}</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            };
            return (
              <div className="criteria-schedule-section">
                <details className="criteria-tab-intro">
                  <summary>
                    <strong>{t('monitoring:help.scheduleTitle')}</strong> {t('monitoring:help.scheduleTab')} <em className="criteria-intro-more">{t('monitoring:help.clickForDetail')}</em>
                  </summary>
                  <div className="criteria-intro-body">
                    <ul>
                      <li><strong>{t('monitoring:act.enable')}</strong>{t('monitoring:help.enabledDesc')}</li>
                      <li><strong>{t('monitoring:crit.intervalSec')}</strong>{t('monitoring:help.intervalDesc')}</li>
                      <li><strong>{t('monitoring:crit.retentionDaysSize')}</strong>{t('monitoring:help.retentionDesc')}</li>
                    </ul>
                    <div style={{ marginTop: 6 }}>
                      {t('monitoring:crit.perInstanceNote')}
                    </div>
                  </div>
                </details>
                {!selectedInstanceId && (
                  <p className="criteria-empty-hint" style={{ marginTop: 8 }}>{t('monitoring:crit2.pickConn')}</p>
                )}
                {selectedInstanceId && groupConfigs.length === 0 && (
                  <p className="criteria-empty-hint" style={{ marginTop: 8 }}>{t('monitoring:status.loadingInstance')}</p>
                )}
                {selectedInstanceId && groupConfigs.length > 0 && (
                  <div className="criteria-sched-wrap">
                  <table className="criteria-sched-table">
                    <thead>
                      <tr>
                        <th>{t('monitoring:crit2.colCriterion')}</th>
                        <th>{t('monitoring:crit2.colState')}</th>
                        <th>{t('monitoring:crit.interval')}</th>
                        <th>{t('monitoring:crit.retention')}</th>
                        <th>{t('monitoring:history.lastRun')}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>{groupConfigs.map(cfg => renderRow(cfg.groupId))}</tbody>
                  </table>
                  </div>
                )}
              </div>
            );
          })()}

            </div>
          </section>
        </div>

        <div className="criteria-modal-footer">
          <button className="app-btn criteria-btn criteria-btn-secondary" onClick={onClose} disabled={loading}>
            {t('monitoring:act.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

// 편집기에 글자를 칠 때마다 App 이 다시 그려져도 값이 같으면 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 입력 지연)
export default memoWindow(MonitoringCriteriaModal);
